/**
 * The words and rules behind the idea card (DESIGN.md "The Idea Card"), kept
 * free of React so scripts/test-ideas.mjs can check them.
 *
 * Everything here reads real fields only. The backend has no author field on
 * an idea and no recommendation (backend #124), so provenance comes from the
 * reference the agent attached and nothing else.
 */

export type IdeaSourceKind = "trend" | "signal" | "universe" | "web" | "media" | "reference";

type ReferenceLike = {
  kind?: "trend" | "reference" | "media" | "custom";
  type: string;
  platform: string;
  videoUrl: string | null;
  imageUrl?: string | null;
  thumbnailUrl: string | null;
  items?: ReadonlyArray<unknown>;
};

type IdeaLike = {
  id: string;
  status: string;
  order?: number;
  reference: ReferenceLike | null;
};

type SlotLike = {
  id: string;
  status: string;
  title: string;
  scheduledAt: string;
  order: number;
  ideas?: ReadonlyArray<unknown>;
};

const PLATFORM_NAMES: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  facebook: "Facebook",
  snapchat: "Snapchat",
  pinterest: "Pinterest",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  substack: "Substack",
};

/** "Instagram" for "INSTAGRAM" or "instagram"; null for anything unknown. */
export function platformName(platform: string | null | undefined): string | null {
  if (!platform) return null;
  return PLATFORM_NAMES[platform.trim().toLowerCase()] ?? null;
}

function withArticle(word: string): string {
  return /^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`;
}

/**
 * The source line over the title: "Idea 2 · from an Instagram trend", or
 * "Idea 1 · written by the agent".
 *
 * Only a trend or a saved reference says where an idea came from: the agent
 * attaches those when it writes the idea. Team media and pasted links are
 * proposal media the team chose later (The Copy-Media Independence Rule), so
 * they say nothing about the idea's origin and the line stays "Idea 2".
 */
export function ideaSourceLine(idea: Pick<IdeaLike, "reference">, index: number): string {
  const head = `Idea ${index + 1}`;
  // Every idea is written by the agent (your own words go through it too);
  // only a trend or a saved reference it started from says more.
  const agent = `${head} · written by the agent`;
  const reference = idea.reference;
  if (!reference) return agent;
  const kind = reference.kind ?? "trend";
  if (kind === "trend") {
    const platform = platformName(reference.platform);
    return platform ? `${head} · from ${withArticle(platform)} trend` : `${head} · from a trend`;
  }
  if (kind === "reference") return `${head} · from a saved reference`;
  return agent;
}

const EVIDENCE_KIND: Record<IdeaSourceKind, string> = {
  trend: "Trend",
  signal: "Signal",
  universe: "Universe",
  web: "Web",
  media: "Media",
  reference: "Reference",
};

/** The evidence row's kind, in the credential colour: Trend, Signal, Universe, Web, Media. */
export function evidenceKindLabel(kind: string): string {
  return EVIDENCE_KIND[kind as IdeaSourceKind] ?? "Source";
}

const INTERNAL_ROUTE: Partial<Record<IdeaSourceKind, string>> = {
  trend: "/dashboard/trends",
  reference: "/dashboard/references",
  signal: "/dashboard/market-signals",
  universe: "/dashboard/brand-universe",
};

export type EvidenceTarget = { kind: "external"; href: string } | { kind: "internal"; to: string } | null;

/**
 * Where an evidence row opens: its own url in a new tab, or, with only a refId,
 * the page that holds that kind of item. No url and no refId: no arrow.
 */
export function evidenceTarget(source: { type: string; url?: string; refId?: string }): EvidenceTarget {
  const url = source.url?.trim();
  if (url && /^https?:\/\//i.test(url)) return { kind: "external", href: url };
  if (source.refId) {
    const to = INTERNAL_ROUTE[source.type as IdeaSourceKind];
    if (to) return { kind: "internal", to };
  }
  return null;
}

/**
 * Suggested hooks, folded. The client only ever sees hooks[0] (the client card
 * and the proposal read hooks[0]), so the fold shows that one and "2 more
 * hooks" unfolds the rest.
 */
export function foldHooks(hooks: readonly string[], open: boolean): { shown: string[]; more: number; moreLabel: string | null } {
  const clean = hooks.map((hook) => hook.trim()).filter(Boolean);
  if (open || clean.length <= 1) return { shown: clean, more: 0, moreLabel: null };
  const more = clean.length - 1;
  return { shown: clean.slice(0, 1), more, moreLabel: `${more} more ${more === 1 ? "hook" : "hooks"}` };
}

/**
 * "Which idea does it replace?" starts on the lowest-numbered idea that is not
 * the selected one and is not already being rewritten.
 */
export function defaultReplaceIdea(ideas: readonly Pick<IdeaLike, "id" | "status">[], selectedId: string | null): string | null {
  const free = ideas.filter((idea) => idea.status !== "WORKING");
  return (free.find((idea) => idea.id !== selectedId) ?? free[0] ?? null)?.id ?? null;
}

/** Whether a post is waiting for a pick: ideas written, none picked. */
export function awaitsPick(slot: Pick<SlotLike, "status" | "ideas">): boolean {
  return slot.status === "IDEATION" && (slot.ideas?.length ?? 0) > 0;
}

/**
 * The batch pick walk: the next post that still waits for a pick, after this
 * one in the month's order, then from the start of the month. Null when this
 * was the last.
 */
export function nextWalkPost<T extends SlotLike>(ordered: readonly T[], currentId: string): T | null {
  const at = ordered.findIndex((slot) => slot.id === currentId);
  const after = at >= 0 ? ordered.slice(at + 1) : ordered;
  const before = at >= 0 ? ordered.slice(0, at) : [];
  return [...after, ...before].find((slot) => slot.id !== currentId && awaitsPick(slot)) ?? null;
}

/** The walk's toast: "Idea 2 picked. Next: {title}". */
export function walkToast(index: number, next: { title: string } | null): string {
  const picked = `Idea ${index + 1} picked. Creo writes the brief.`;
  return next ? `${picked} Next: ${next.title}` : picked;
}

/**
 * A reference whose original disappeared keeps its label and ratio but comes
 * back with no playable URL at all.
 */
export function referenceGone(reference: ReferenceLike | null): boolean {
  if (!reference) return false;
  const hasItems = (reference.items?.length ?? 0) > 0;
  return !reference.videoUrl && !reference.imageUrl && !reference.thumbnailUrl && !hasItems;
}

/** "Trend reference", "Saved reference", "Team media" or "Pasted link". */
export function referenceKindLabel(reference: Pick<ReferenceLike, "kind">): string {
  const kind = reference.kind ?? "trend";
  if (kind === "reference") return "Saved reference";
  if (kind === "media") return "Team media";
  if (kind === "custom") return "Pasted link";
  return "Trend reference";
}

/** "Planned 16:9, needs adapting" when the source ratio is not the planned output. */
export function adaptationNote(sourceRatio: string | null | undefined, outputRatio: string): string | null {
  if (!sourceRatio || sourceRatio === outputRatio) return null;
  return `Planned ${outputRatio}, needs adapting`;
}

/** The specs line: "Heritage Roots · Video 16:9 · 2m 30s", missing parts left out. */
export function specsLine(parts: { pillar?: string | null; format: string; length?: string | null }): string {
  return [parts.pillar?.trim() || null, parts.format, parts.length || null].filter(Boolean).join(" · ");
}
